import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit3,
  FiPrinter,
  FiMail,
  FiDollarSign,
  FiCreditCard,
  FiCalendar,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiDownload,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiPlus,
  FiFileText
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import { useFacturas } from '../../hooks/useFacturas';

const FacturaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getFacturaById, markAsPaid, printFactura, downloadFactura, isLoading, error } = useFacturas();
  const [factura, setFactura] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: 'efectivo',
    reference: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadFacturaDetalle();
  }, [id]);

  const loadFacturaDetalle = async () => {
    const result = await getFacturaById(id);
    if (result.success) {
      setFactura(result.data);
    }
    setInitialLoading(false);
  };

  const handlePrintInvoice = async () => {
    const result = await printFactura(id);
    if (!result.success) {
      alert(result.message || 'Error al generar el PDF. Por favor intente nuevamente.');
    }
  };

  const handleDownloadInvoice = async () => {
    const result = await downloadFactura(id, factura.numeroFactura);
    if (!result.success) {
      alert(result.message || 'Error al descargar el PDF. Por favor intente nuevamente.');
    }
  };

  const handleEditFactura = () => {
    navigate(`/system/facturacion/${id}/editar`);
  };

  const handlePayment = () => {
    setPaymentData(prev => ({
      ...prev,
      amount: factura.saldoPendiente
    }));
    setShowPaymentModal(true);
  };

  const handlePartialPayment = () => {
    setPaymentData(prev => ({
      ...prev,
      amount: 0
    }));
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    const paymentInfo = {
      payment_amount: parseFloat(paymentData.amount),
      payment_method: paymentData.method,
      payment_date: paymentData.date,
      payment_reference: paymentData.reference,
      notes: paymentData.notes
    };

    const result = await markAsPaid(id, paymentInfo);

    if (result.success) {
      setFactura(result.data);
      setShowPaymentModal(false);
      setPaymentData({
        amount: 0,
        method: 'efectivo',
        reference: '',
        notes: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
  };

  const getStatusColor = (estado) => {
    const statusMap = {
      'draft': 'secondary',
      'pending': 'warning',
      'overdue': 'danger',
      'paid': 'success',
      'partial': 'info',
      'cancelled': 'secondary'
    };
    return statusMap[estado?.toLowerCase()] || 'default';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading || initialLoading) {
    return <LoadingGrid message="Cargando información de la factura..." />;
  }

  if (!factura) {
    return (
      <div className="error-container">
        <h2>Factura no encontrada</h2>
        <button onClick={() => navigate('/system/facturacion')} className="btn btn--primary">
          Volver a Facturación
        </button>
      </div>
    );
  }

  return (
    <div className="factura-detalle">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={() => navigate('/system/facturacion')}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          Factura {factura.numeroFactura}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handlePrintInvoice} disabled={isLoading}>
            <FiPrinter size={16} />
            Imprimir
          </button>
          <button className="btn btn--outline" onClick={handleDownloadInvoice} disabled={isLoading}>
            <FiDownload size={16} />
            Descargar PDF
          </button>
          {factura.saldoPendiente > 0 && (
            <button className="btn btn--primary" onClick={handlePayment}>
              <FiDollarSign size={16} />
              Registrar Pago
            </button>
          )}
        </div>
      </div>

      {/* Card 1: Información Principal de la Factura */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiFileText size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">Factura {factura.numeroFactura}</h3>
            <p className="invoice-header-subtitle">
              <span className={`status-badge status-badge--${getStatusColor(factura.estado)}`}>
                {factura.estado?.toUpperCase()}
              </span>
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Cliente */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiUser size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Cliente</label>
              <div className="invoice-field-value">{factura.clienteNombre}</div>
            </div>
          </div>

          {/* Fecha de Emisión */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCalendar size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fecha de Emisión</label>
              <div className="invoice-field-value">{formatDate(factura.fecha)}</div>
            </div>
          </div>

          {/* Fecha de Vencimiento */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCalendar size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fecha de Vencimiento</label>
              <div className="invoice-field-value">{formatDate(factura.fechaVencimiento)}</div>
            </div>
          </div>

          {/* Método de Pago */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCreditCard size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Método de Pago</label>
              <div className="invoice-field-value">{factura.metodoPago || 'No especificado'}</div>
            </div>
          </div>

          {/* Notas */}
          {factura.notas && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas</label>
                <div className="invoice-field-value">{factura.notas}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Tabla de Factura */}
      {factura.items && factura.items.length > 0 ? (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Productos en la Factura</h3>
              <p className="table-header-subtitle">{factura.items.length} producto(s)</p>
            </div>
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th className="th-product">Producto</th>
                  <th className="th-qty">Cantidad</th>
                  <th className="th-price">Precio Unit.</th>
                  <th className="th-discount">Desc.</th>
                  <th className="th-total">Total</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {factura.items.map((item, index) => (
                  <tr key={index} className="invoice-table-row">
                    <td className="td-product">
                      <div className="product-cell">
                        <div className="product-cell-icon">
                          <FiPackage size={16} />
                        </div>
                        <div className="product-cell-info">
                          <div className="product-cell-name">{item.description || item.product_name}</div>
                          <div className="product-cell-code">{item.product_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="td-qty">
                      <span className="qty-badge">{parseFloat(item.quantity)}</span>
                    </td>
                    <td className="td-price">
                      <span className="price-value">{formatCurrency(item.unit_price)}</span>
                    </td>
                    <td className="td-discount">
                      {item.discount_percentage > 0 ? (
                        <span className="discount-badge">{item.discount_percentage}%</span>
                      ) : (
                        <span className="no-discount">—</span>
                      )}
                    </td>
                    <td className="td-total">
                      <span className="total-value">{formatCurrency(item.total_price)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="invoice-totals-section">
            <div className="totals-container">
              <div className="totals-row">
                <span className="totals-label">Subtotal</span>
                <span className="totals-value">{formatCurrency(factura.subtotal)}</span>
              </div>
              {factura.descuento > 0 && (
                <div className="totals-row totals-row--discount">
                  <span className="totals-label">Descuento</span>
                  <span className="totals-value">-{formatCurrency(factura.descuento)}</span>
                </div>
              )}
              {factura.costoEnvio > 0 && (
                <div className="totals-row">
                  <span className="totals-label">Envío</span>
                  <span className="totals-value">{formatCurrency(factura.costoEnvio)}</span>
                </div>
              )}
              <div className="totals-divider"></div>
              <div className="totals-row totals-row--final">
                <span className="totals-label">Total</span>
                <span className="totals-value">{formatCurrency(factura.total)}</span>
              </div>
              {factura.montoAbonado > 0 && (
                <>
                  <div className="totals-row">
                    <span className="totals-label">Pagado</span>
                    <span className="totals-value">-{formatCurrency(factura.montoAbonado)}</span>
                  </div>
                  <div className="totals-row totals-row--balance">
                    <span className="totals-label">Saldo Pendiente</span>
                    <span className="totals-value">{formatCurrency(factura.saldoPendiente)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon">
            <FiPackage size={64} />
          </div>
          <h3 className="empty-state-title">No hay productos en esta factura</h3>
        </div>
      )}

      {/* Historial de pagos */}
      {factura.pagos && factura.pagos.length > 0 && (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiDollarSign size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Historial de Pagos</h3>
              <p className="table-header-subtitle">
                Total pagado: {formatCurrency(factura.montoAbonado)}
              </p>
            </div>
          </div>
          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th>Fecha</th>
                  <th>Método</th>
                  <th>Referencia</th>
                  <th className="text-right">Monto</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {factura.pagos.map((pago, index) => (
                  <tr key={index} className="invoice-table-row">
                    <td>{formatDate(pago.payment_date)}</td>
                    <td>{pago.payment_method}</td>
                    <td>{pago.reference_number || '-'}</td>
                    <td className="text-right">
                      <span className="total-value">{formatCurrency(pago.amount)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de Pago */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Registrar Pago"
        size="medium"
      >
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Monto del Pago *</label>
            <input
              type="number"
              className="form-input"
              value={paymentData.amount}
              onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              min="0"
              max={factura.saldoPendiente}
              step="0.01"
            />
            <small className="form-helper">
              Saldo pendiente: {formatCurrency(factura.saldoPendiente)}
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Método de Pago *</label>
            <select
              className="form-input"
              value={paymentData.method}
              onChange={(e) => setPaymentData(prev => ({ ...prev, method: e.target.value }))}
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="cheque">Cheque</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Fecha del Pago</label>
            <input
              type="date"
              className="form-input"
              value={paymentData.date}
              onChange={(e) => setPaymentData(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Referencia</label>
            <input
              type="text"
              className="form-input"
              value={paymentData.reference}
              onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="Número de cheque, transferencia, etc."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="form-input"
              value={paymentData.notes}
              onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notas adicionales sobre el pago"
              rows={3}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowPaymentModal(false)}
          >
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={processPayment}
            disabled={!paymentData.amount || paymentData.amount <= 0 || isLoading}
          >
            <FiCheck size={16} />
            Registrar Pago
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FacturaDetalle;